import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Loader2, ArrowRight } from "lucide-react";
import { useLang, CONTACT } from "../i18n";
import { RevealText } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contacto() {
  const { t } = useLang();
  const c = t.contacto;
  const [form, setForm] = useState({ name: "", phone: "", email: "", project: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(c.success);
      setForm({ name: "", phone: "", email: "", project: "" });
    } catch (err) {
      toast.error(c.error);
    } finally {
      setLoading(false);
    }
  };

  const fields = [
    { name: "name", label: c.name, type: "text" },
    { name: "phone", label: c.phone, type: "tel" },
    { name: "email", label: c.email, type: "email" },
  ];

  return (
    <section id="contacto" className="relative py-24 md:py-32 grain">
      <div className="mx-auto max-w-7xl px-5 md:px-8 grid lg:grid-cols-2 gap-14 lg:gap-20">
        <div>
          <div className="flex items-center gap-3 mb-8">
            <span className="h-px w-10 bg-brand-orange" />
            <span className="text-xs md:text-sm font-bold tracking-[0.25em] uppercase text-brand-orange">{c.overline}</span>
          </div>
          <RevealText
            as="h2"
            text={c.title}
            className="font-display font-semibold tracking-tighter leading-[0.95] text-4xl md:text-5xl lg:text-6xl block"
          />
          <p className="mt-6 max-w-md text-steel-300 md:text-lg leading-relaxed">{c.sub}</p>

          <div className="mt-12 space-y-6">
            <a href={`tel:${CONTACT.whatsapp}`} data-testid="contact-phone" className="flex items-center gap-4 group">
              <span className="h-11 w-11 border border-border grid place-items-center group-hover:border-brand-orange transition-colors"><Phone className="h-5 w-5 text-brand-orange" /></span>
              <span className="text-lg group-hover:text-brand-orange transition-colors">{CONTACT.phoneDisplay}</span>
            </a>
            <a href={`mailto:${CONTACT.email}`} data-testid="contact-email" className="flex items-center gap-4 group">
              <span className="h-11 w-11 border border-border grid place-items-center group-hover:border-brand-orange transition-colors"><Mail className="h-5 w-5 text-brand-orange" /></span>
              <span className="text-lg group-hover:text-brand-orange transition-colors">{CONTACT.email}</span>
            </a>
            <div className="flex items-center gap-4">
              <span className="h-11 w-11 border border-border grid place-items-center"><MapPin className="h-5 w-5 text-brand-orange" /></span>
              <span className="text-lg">{CONTACT.city}</span>
            </div>
          </div>
        </div>

        <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-8">
          {fields.map((f) => (
            <div key={f.name} className="relative">
              <label className="block text-xs font-bold tracking-[0.15em] uppercase text-steel-400 mb-3">{f.label}</label>
              <input
                required
                type={f.type}
                name={f.name}
                value={form[f.name]}
                onChange={onChange}
                data-testid={`input-${f.name}`}
                className="w-full bg-transparent border-b border-border py-3 text-lg text-white placeholder-steel-500 focus:outline-none focus:border-brand-orange transition-colors duration-300"
              />
            </div>
          ))}
          <div>
            <label className="block text-xs font-bold tracking-[0.15em] uppercase text-steel-400 mb-3">{c.project}</label>
            <textarea
              required
              name="project"
              rows={4}
              value={form.project}
              onChange={onChange}
              placeholder={c.projectPh}
              data-testid="input-project"
              className="w-full bg-transparent border-b border-border py-3 text-lg text-white placeholder-steel-500 focus:outline-none focus:border-brand-orange transition-colors duration-300 resize-none"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="submit-contact"
            className="group inline-flex items-center gap-3 bg-brand-orange text-black font-semibold px-8 py-4 rounded-full hover:bg-white transition-colors duration-300 disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
            {loading ? c.sending : c.send}
            {!loading && <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />}
          </button>
        </form>
      </div>
    </section>
  );
}
